import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Slice } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { CellLabel, EntityListSection, EntityValuePillList, useLang, usePageEntity } from '@shapeable/ui';
import { Region } from '@shapeable/kelp-forest-alliance-types';

const cls = classNames('slice-region-data');

// -------- Types -------->

// -------- Props -------->

export type SliceRegionDataProps = Classable & HasChildren & { 
  entity?: Slice;
};

export const SliceRegionDataDefaultProps: Omit<SliceRegionDataProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(3)};
  `,
});

const LabelStyles = breakpoints({
  base: css`
    font-size: 1em;
    color: ${theme.COLOR('mid')};
    text-transform: none;
  `,
});

const ResultStyles = breakpoints({
  base: css`
    font-weight: 500;
    font-size: 1em;
  `,
});

const MetricStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.UNIT(2)};
    padding-top: ${theme.UNIT(3)};
    ${theme.H_DOT_BG('line-subtle')};
  `,
});

const PopulationNearKelpStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});

const CoastlineLengthStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});

const OceanWarmingRateStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});

const GdpWithin50kmKelpStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});

const KelpAreaHectaresStyles = breakpoints({
  base: css`   
    ${MetricStyles}
  `,
});

const AudienceReachedStyles = breakpoints({
  base: css`    
    ${MetricStyles}
  `,
});

const KeySpeciesStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(3)};
    ${theme.H_DOT_BG('line-subtle')};
  `,
});

const SpeciesStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(3)};
  `,
});

const CarbonSequestrationMegaTonnesStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});


// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Label: styled(CellLabel)`${LabelStyles}`,
    Result: styled.span`${ResultStyles}`,

    PopulationNearKelp: styled.div`${PopulationNearKelpStyles}`,
    CoastlineLength: styled.div`${CoastlineLengthStyles}`,
    OceanWarmingRate: styled.div`${OceanWarmingRateStyles}`,
    GdpWithin50kmKelp: styled.div`${GdpWithin50kmKelpStyles}`,
    KelpAreaHectares: styled.div`${KelpAreaHectaresStyles}`,
    AudienceReached: styled.div`${AudienceReachedStyles}`,
    CarbonSequestrationMegaTonnes: styled.div`${CarbonSequestrationMegaTonnesStyles}`,
    KeySpecies: styled.div`${KeySpeciesStyles}`,
      Species: styled(EntityValuePillList)`${SpeciesStyles}`,
};

export const SliceRegionData: Shapeable.FC<SliceRegionDataProps> = (props) => {
  const { className, children, entity } = props;

  const activeEntity = usePageEntity() as Region;

  const t = useLang();

  const { populationNearKelp, coastlineLengthkm, oceanWarmingRate, 
    kelpAreaHectares, gdpWithin50kmKelp, species = [], carbonSequestrationMegaTonnes } = activeEntity || {};
  
  return (
    <My.Container className={cls.name(className)}>
      {
        !!populationNearKelp &&
        <My.PopulationNearKelp>
          <My.Label>{t('PEOPLE LIVING WITHIN 50KM OF KELP FORESTS:')}</My.Label>
          <My.Result>{populationNearKelp?.toLocaleString()}</My.Result>
        </My.PopulationNearKelp>
      }
      {
        !!coastlineLengthkm &&
        <My.CoastlineLength>
          <My.Label>{t('COASTLINE (KM):')}</My.Label>
          <My.Result>{coastlineLengthkm?.toLocaleString()} miles</My.Result>
        </My.CoastlineLength>
      }
      {
        !!gdpWithin50kmKelp &&
        <My.GdpWithin50kmKelp>
          <My.Label>{t('GDP(B) WITHIN 50KM OF KELP:')}</My.Label>
          <My.Result>${gdpWithin50kmKelp?.toLocaleString()} billion</My.Result>
        </My.GdpWithin50kmKelp>
      }
      {
        !!oceanWarmingRate &&
        <My.OceanWarmingRate>
          <My.Label>{t('OCEAN WARMING RATE BY 2100 (°C):')}</My.Label>
          <My.Result>{oceanWarmingRate?.toLocaleString()} °C</My.Result>
        </My.OceanWarmingRate>
      }
      {
        !!kelpAreaHectares &&
        <My.KelpAreaHectares>
          <My.Label>{t('KM² OF KELP:')}</My.Label>
          <My.Result>{kelpAreaHectares?.toLocaleString()}</My.Result>
        </My.KelpAreaHectares>
      }
      {
        !!carbonSequestrationMegaTonnes &&
        <My.CarbonSequestrationMegaTonnes>
          <My.Label>{t('CARBON SEQUESTRATION (MtCO2e):')}</My.Label>
          <My.Result>{carbonSequestrationMegaTonnes?.toLocaleString()}</My.Result>
        </My.CarbonSequestrationMegaTonnes>
      }
      {
        !!species.length && 
        <My.KeySpecies>
          <My.Label>{t('Key Species:')}</My.Label>
          <My.Species
            showLabel={false}
            items={species}
          />
        </My.KeySpecies>
        
      }
      
    </My.Container>
  )
};

SliceRegionData.cls = cls;